import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Search, MapPin, Star, Loader2, Users, Bed, 
  Building2, Home, TreePine, Briefcase, ChevronRight, Zap, Shield, HeartHandshake
} from 'lucide-react';
import { format } from 'date-fns';
import { api } from '@/App';
import PortalDateRangePicker from './PortalDateRangePicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full URL for logo
const getLogoUrl = (url) => {
  if (!url) return '';
  // Already a full URL (R2 or external)
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // Local API path - prepend backend URL
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  // Other local path - prepend backend URL with /api
  if (url.startsWith('/')) return `${BACKEND_URL}/api${url}`;
  // Relative path without leading slash
  return `${BACKEND_URL}/api/uploads/${url}`;
};

// Property type images
const PROPERTY_TYPES = [
  { 
    label: 'Hotels', 
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&h=300&fit=crop',
    icon: Bed
  },
  { 
    label: 'Apartments', 
    image: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=400&h=300&fit=crop',
    icon: Building2
  },
  { 
    label: 'Resorts', 
    image: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=400&h=300&fit=crop',
    icon: TreePine
  },
  { 
    label: 'Villas', 
    image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=400&h=300&fit=crop',
    icon: Home
  },
];

const BookingPortalHome = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [searchForm, setSearchForm] = useState({
    location: searchParams.get('location') || '',
    guests: searchParams.get('guests') || '2',
    rooms: '1'
  });
  
  const [dateRange, setDateRange] = useState(() => {
    const ci = searchParams.get('checkIn') || searchParams.get('check_in');
    const co = searchParams.get('checkOut') || searchParams.get('check_out');
    return {
      from: ci ? new Date(ci + 'T00:00:00') : undefined,
      to: co ? new Date(co + 'T00:00:00') : undefined,
    };
  });
  
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  
  // Initialize portal settings from cache or with null (no defaults to avoid flash)
  const [portalSettings, setPortalSettings] = useState(() => {
    try {
      const cached = localStorage.getItem('portal_settings_cache');
      if (cached) {
        const parsed = JSON.parse(cached);
        // Only use cache if it has the required fields
        if (parsed.primary_color && parsed.secondary_color) {
          return parsed;
        }
      }
    } catch (e) {}
    return null;
  });

  useEffect(() => {
    fetchPortalData();
  }, []);

  const fetchPortalData = async () => {
    try {
      const [propertiesRes, settingsRes] = await Promise.all([
        api.get('/portal/properties?limit=8'),
        api.get('/portal/settings').catch(() => ({ data: {} }))
      ]);
      setProperties(propertiesRes.data.properties || []);
      if (settingsRes.data && settingsRes.data.primary_color) {
        const newSettings = {
          portal_name: 'Quick Book',
          primary_color: '#d4a373',
          secondary_color: '#2d3748',
          ...settingsRes.data
        };
        setPortalSettings(newSettings);
        // Cache settings for instant load next time
        localStorage.setItem('portal_settings_cache', JSON.stringify(newSettings));
      } else if (!portalSettings) {
        // Fallback defaults if API returns nothing and no cache
        setPortalSettings({
          portal_name: 'Quick Book',
          primary_color: '#d4a373',
          secondary_color: '#2d3748'
        });
      }
      setSettingsLoaded(true);
    } catch (error) {
      console.error('Failed to fetch portal data:', error);
      // Set defaults on error if not already set
      if (!portalSettings) {
        setPortalSettings({
          portal_name: 'Quick Book',
          primary_color: '#d4a373',
          secondary_color: '#2d3748'
        });
      }
      setSettingsLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (searchForm.location) params.set('location', searchForm.location);
    if (dateRange?.from) params.set('check_in', format(dateRange.from, 'yyyy-MM-dd'));
    if (dateRange?.to) params.set('check_out', format(dateRange.to, 'yyyy-MM-dd'));
    if (searchForm.guests) params.set('guests', searchForm.guests);
    navigate(`/search?${params.toString()}`);
  };

  const handlePropertyClick = (property) => {
    const params = new URLSearchParams();
    if (dateRange?.from) params.set('check_in', format(dateRange.from, 'yyyy-MM-dd'));
    if (dateRange?.to) params.set('check_out', format(dateRange.to, 'yyyy-MM-dd'));
    if (searchForm.guests) params.set('guests', searchForm.guests);
    const qs = params.toString();
    navigate(`/property/${property.id}${qs ? `?${qs}` : ''}`);
  };

  if (loading || !portalSettings) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-800">
        <Loader2 className="h-12 w-12 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: portalSettings.font_family ? `'${portalSettings.font_family}', sans-serif` : "'Inter', sans-serif" }}>
      {/* Header */}
      <header style={{ backgroundColor: portalSettings.header_bg_color || portalSettings.secondary_color }}>
        <div className="max-w-[1200px] mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div 
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => navigate('/')}
              data-testid="logo"
            >
              {portalSettings.portal_logo ? (
                <img 
                  src={getLogoUrl(portalSettings.portal_logo)} 
                  alt={portalSettings.portal_name || 'Logo'}
                  className="h-10 w-auto max-w-[160px]"
                  style={{ objectFit: 'contain', objectPosition: 'left center' }}
                  data-testid="portal-logo-img"
                />
              ) : (
                <>
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: portalSettings.primary_color }}
                  >
                    <Zap className="h-6 w-6 text-white" />
                  </div>
                  <span 
                    className="text-2xl font-bold tracking-tight"
                    style={{ color: portalSettings.header_text_color || '#ffffff' }}
                  >
                    {portalSettings.portal_name || 'Quick Book'}
                  </span>
                </>
              )}
            </div>
            
            {/* Auth Buttons */}
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                className="hover:bg-white/10 text-sm font-medium"
                style={{ color: portalSettings.header_text_color || '#ffffff' }}
                onClick={() => navigate('/login')}
                data-testid="login-btn"
              >
                Sign in
              </Button>
              <Button 
                className="text-white hover:opacity-90 text-sm font-medium"
                style={{ backgroundColor: portalSettings.primary_color }}
                onClick={() => navigate('/signup')}
                data-testid="register-btn"
              >
                Register
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div 
        className="relative overflow-hidden pb-28"
        style={{ 
          background: portalSettings.hero_gradient_enabled !== false 
            ? (portalSettings.hero_gradient_direction === 'radial'
              ? `radial-gradient(circle, ${portalSettings.banner_bg_color || portalSettings.secondary_color} 0%, ${portalSettings.hero_gradient_end || '#1a202c'} 100%)`
              : `linear-gradient(${portalSettings.hero_gradient_direction || '135deg'}, ${portalSettings.banner_bg_color || portalSettings.secondary_color} 0%, ${portalSettings.hero_gradient_end || '#1a202c'} 100%)`)
            : (portalSettings.banner_bg_color || portalSettings.secondary_color)
        }}
      >
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10" 
          style={{ backgroundColor: portalSettings.primary_color, filter: 'blur(100px)' }} 
        />
        <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full opacity-10" 
          style={{ backgroundColor: portalSettings.primary_color, filter: 'blur(80px)' }} 
        />
        
        <div className="max-w-[1200px] mx-auto px-4 pt-12 relative z-10">
          {/* Headline */}
          <div className="text-center mb-10">
            <h1 
              className="text-4xl md:text-5xl font-bold mb-4 tracking-tight"
              style={{ color: portalSettings.banner_text_color || '#ffffff' }}
            >
              {portalSettings.hero_title || 'Find Your Perfect Stay'}
            </h1>
            <p 
              className="text-lg max-w-xl mx-auto opacity-70"
              style={{ color: portalSettings.banner_text_color || '#ffffff' }}
            >
              {portalSettings.hero_subtitle || 'Compare prices across platforms and book directly for the best deals on hotels, resorts, and more.'}
            </p>
          </div>
        </div>
      </div>

      {/* Search Bar - Overlapping */}
      <div className="max-w-[1000px] mx-auto px-4 -mt-16 relative z-20">
        <div 
          className="p-1.5 rounded-2xl shadow-2xl"
          style={{ backgroundColor: portalSettings.primary_color }}
        >
          <div className="bg-white rounded-xl p-2">
            <form onSubmit={handleSearch} className="flex flex-col lg:flex-row gap-2">
              {/* Location Input */}
              <div className="flex-1 min-w-0 bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 flex-shrink-0" style={{ color: portalSettings.primary_color }} />
                  <div className="flex-1 min-w-0">
                    <label className="text-xs text-gray-500 font-medium block mb-0.5">Destination</label>
                    <Input
                      placeholder="Where are you going?"
                      value={searchForm.location}
                      onChange={(e) => setSearchForm({ ...searchForm, location: e.target.value })}
                      className="border-0 p-0 h-auto text-base font-semibold placeholder:text-gray-400 focus-visible:ring-0 shadow-none bg-transparent"
                      data-testid="search-location"
                    />
                  </div>
                </div>
              </div>

              {/* Date Range */}
              <div className="flex-1 min-w-0 bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors">
                <PortalDateRangePicker 
                  dateRange={dateRange} 
                  onDateRangeChange={setDateRange} 
                  variant="quickbook"
                  primaryColor={portalSettings.primary_color}
                />
              </div>

              {/* Guests */}
              <div className="w-full lg:w-40 bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 flex-shrink-0" style={{ color: portalSettings.primary_color }} />
                  <div className="flex-1">
                    <label className="text-xs text-gray-500 font-medium block mb-0.5">Guests</label>
                    <Input
                      type="number"
                      min="1"
                      max="30"
                      value={searchForm.guests}
                      onChange={(e) => setSearchForm({ ...searchForm, guests: e.target.value })}
                      className="border-0 p-0 h-auto text-base font-semibold focus-visible:ring-0 shadow-none bg-transparent"
                      data-testid="search-guests"
                    />
                  </div>
                </div>
              </div>

              {/* Search Button */}
              <div className="flex-shrink-0">
                <Button 
                  type="submit"
                  className="w-full lg:w-auto h-full min-h-[60px] px-8 text-white font-bold text-lg rounded-lg transition-all hover:scale-105"
                  style={{ backgroundColor: portalSettings.primary_color }}
                  data-testid="search-btn"
                >
                  <Search className="h-5 w-5 mr-2" />
                  Search
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Browse by Property Type */}
      <section className="max-w-[1200px] mx-auto px-4 py-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Browse by property type
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {PROPERTY_TYPES.map((type) => (
            <div 
              key={type.label}
              className="group cursor-pointer"
              onClick={() => {
                setSearchForm(prev => ({ ...prev, location: type.label }));
                handleSearch();
              }}
              data-testid={`property-type-${type.label.toLowerCase()}`}
            >
              <div className="relative overflow-hidden rounded-xl mb-3 shadow-md">
                <img 
                  src={type.image} 
                  alt={type.label}
                  className="w-full h-40 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-3 left-3">
                  <h3 className="font-bold text-white text-lg">{type.label}</h3>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Popular Properties */}
      {properties.length > 0 && (
        <section className="max-w-[1200px] mx-auto px-4 pb-16">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Popular destinations
            </h2>
            <Button 
              variant="link" 
              className="font-medium p-0"
              style={{ color: portalSettings.primary_color }}
              onClick={() => navigate('/search')}
            >
              View all <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {properties.slice(0, 4).map((property) => (
              <div 
                key={property.id}
                className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group"
                onClick={() => handlePropertyClick(property)}
                data-testid={`property-card-${property.id}`}
              >
                {/* Property Image */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={property.photos?.[0] || property.image || 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&h=300&fit=crop'}
                    alt={property.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div 
                    className="absolute top-3 right-3 text-white text-xs font-bold px-2 py-1 rounded-md"
                    style={{ backgroundColor: portalSettings.primary_color }}
                  >
                    {property.rating || '8.5'} ★
                  </div>
                </div>
                
                {/* Property Info */}
                <div className="p-4">
                  <h3 className="font-bold text-gray-900 text-base mb-1 line-clamp-1 group-hover:text-orange-600 transition-colors">
                    {property.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-3 flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" />
                    {property.city || property.location}
                  </p>
                  
                  {/* Price */}
                  <div className="flex items-end justify-between border-t pt-3">
                    <div>
                      <span className="text-xs text-gray-500">Starting from</span>
                      <p className="text-xl font-bold" style={{ color: portalSettings.secondary_color }}>
                        ₹{(property.min_price || property.starting_price || 3500).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <span className="text-xs text-gray-400">per night</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Why Book Section */}
      <section className="py-16" style={{ backgroundColor: portalSettings.secondary_color + '08' }}>
        <div className="max-w-[1200px] mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-10 text-center">
            Why book with {portalSettings.portal_name || 'Quick Book'}?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center bg-white rounded-xl p-6 shadow-sm">
              <div 
                className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: portalSettings.primary_color + '15' }}
              >
                <Star className="h-7 w-7" style={{ color: portalSettings.primary_color }} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Best Price Guarantee</h3>
              <p className="text-sm text-gray-500">Compare competitor prices and always get the best deal. We'll match any lower price.</p>
            </div>
            <div className="text-center bg-white rounded-xl p-6 shadow-sm">
              <div 
                className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: portalSettings.primary_color + '15' }}
              >
                <Shield className="h-7 w-7" style={{ color: portalSettings.primary_color }} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Free Cancellation</h3>
              <p className="text-sm text-gray-500">Flexible booking options on most rooms. Cancel for free up to 24 hours before.</p>
            </div>
            <div className="text-center bg-white rounded-xl p-6 shadow-sm">
              <div 
                className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: portalSettings.primary_color + '15' }}
              >
                <HeartHandshake className="h-7 w-7" style={{ color: portalSettings.primary_color }} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">24/7 Customer Support</h3>
              <p className="text-sm text-gray-500">We're here to help, anytime. Reach out via chat, email, or phone.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10" style={{ backgroundColor: portalSettings.footer_bg_color || portalSettings.secondary_color }}>
        <div className="max-w-[1200px] mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: portalSettings.primary_color }}
              >
                <Zap className="h-5 w-5 text-white" />
              </div>
              <span 
                className="text-xl font-bold tracking-tight"
                style={{ color: portalSettings.footer_text_color || '#ffffff' }}
              >
                {portalSettings.portal_name || 'Quick Book'}
              </span>
            </div>
            <div 
              className="flex items-center gap-6 text-sm opacity-70"
              style={{ color: portalSettings.footer_text_color || '#ffffff' }}
            >
              <a href="#" className="hover:opacity-100 transition-opacity">About</a>
              <a href="#" className="hover:opacity-100 transition-opacity">Help</a>
              <a href="#" className="hover:opacity-100 transition-opacity">Terms</a>
              <a href="#" className="hover:opacity-100 transition-opacity">Privacy</a>
              <button 
                onClick={() => navigate('/partner')} 
                className="hover:opacity-100 transition-opacity"
                data-testid="partner-portal-link"
              >
                Partner Portal
              </button>
            </div>
            <p 
              className="text-xs opacity-50"
              style={{ color: portalSettings.footer_text_color || '#ffffff' }}
            >
              {portalSettings.footer_text || `© ${new Date().getFullYear()} ${portalSettings.portal_name || 'Quick Book'}. All rights reserved.`}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BookingPortalHome;
